import { AppValidator } from "@/common/app-validate.ts";
import CustomRequiredMarkAntdForm from "@/component/CustomRequiredMarkAntdForm.tsx";
import { MANAGER_PATH } from "@/constant/url-path.ts";
import { LicenseRegistrationService } from "@/services/licenseRegistration/LicenseRegistrationService.ts";
import { toast } from "@/utils/toast.tsx";
import { Button, Col, Form, Input, InputNumber, Modal, Row } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const ExtendButton = ({
  detailRegistration,
  buttonText,
  buttonClass,
}: {
  detailRegistration?: LicenseRegistrationDetail;
  buttonText?: string;
  buttonClass?: string;
}) => {
  const { t } = useTranslation();
  const validators = AppValidator(t);
  const navigation = useNavigate();

  buttonText = buttonText ? buttonText : t("button.extendLicense");
  buttonClass = buttonClass
    ? buttonClass
    : "bg-[#FF9330] hover:!bg-[#FF9330]/80";

  const [licenseRegistrationInfoForm] = Form.useForm();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  useEffect(() => {
    if (detailRegistration) {
      const macsString = detailRegistration.addressMac
        ? detailRegistration.addressMac.trim().replace(/^;|;$/g, "")
        : null;
      const macList = macsString ? macsString.split(";") : [];
      detailRegistration.numberOfLicense = macList.length;
    }
  }, []);

  const openModal = () => {
    setIsOpen(true);
  };

  const handleCancelModal = () => {
    setIsOpen(false);
    licenseRegistrationInfoForm.resetFields();
  };

  const handelExtendLicense = () => {
    licenseRegistrationInfoForm.validateFields().then(async (value) => {
      const request: CreateLicenseRegistrationRequest = {
        ...detailRegistration,
        ...value,
      };
      const res: SuccessResponse | null | undefined =
        await LicenseRegistrationService.create(request);
      if (res) {
        navigation(MANAGER_PATH.LICENSE_REGISTRATION.LIST);
        toast.success(res?.message || "Success");
      }
    });
  };

  const changeNumberOfLicenseByNumberOfMac = async () => {
    await licenseRegistrationInfoForm
      .validateFields(["addressMac"])
      .then((value) => {
        const macsString = value.addressMac
          ? value.addressMac.trim().replace(/^;|;$/g, "")
          : null;
        const macList = macsString ? macsString.split(";") : [];
        licenseRegistrationInfoForm.setFieldValue(
          "numberOfLicense",
          macList.length,
        );
      })
      .catch(() => {});
  };

  return (
    <>
      <Button
        type={"primary"}
        className={buttonClass}
        onClick={openModal}
        size={"large"}
      >
        {buttonText}
      </Button>
      <Modal
        title={<h2>{t("title.extendLicenseRegistrationModal")}</h2>}
        open={isOpen}
        okText={t("button.create")}
        onCancel={handleCancelModal}
        footer={
          <>
            <Button onClick={handleCancelModal} size={"large"}>
              {t("button.cancel")}
            </Button>
            <Button
              type={"primary"}
              onClick={handelExtendLicense}
              size={"large"}
            >
              {buttonText}
            </Button>
          </>
        }
        width={1000}
        style={{ top: 40 }}
        styles={{ body: { maxHeight: 685, overflow: "auto" } }}
      >
        <Form
          form={licenseRegistrationInfoForm}
          name="extend-license-registration-info-form"
          layout={"vertical"}
          colon={false}
          requiredMark={CustomRequiredMarkAntdForm}
          initialValues={detailRegistration}
          size={"large"}
        >
          <Row>
            <Col span={12} className={"pr-12"}>
              <Row gutter={32}>
                <Col span={12}>
                  <Form.Item<CreateLicenseRegistrationRequest>
                    name="firstName"
                    label={t("field.firstName")}
                    className={"w-full"}
                  >
                    <Input disabled />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item<CreateLicenseRegistrationRequest>
                    name="lastName"
                    label={t("field.lastName")}
                  >
                    <Input disabled />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item<CreateLicenseRegistrationRequest>
                name="registrantMobile"
                label={t("field.mobile")}
              >
                <Input disabled />
              </Form.Item>
              <Form.Item<CreateLicenseRegistrationRequest>
                name="companyName"
                label={t("field.companyName")}
              >
                <Input disabled />
              </Form.Item>
              <Form.Item<CreateLicenseRegistrationRequest>
                name="duration"
                label={t("field.duration")}
              >
                <InputNumber
                  min={0}
                  className={"w-full"}
                  suffix={t("field.months")}
                />
              </Form.Item>
            </Col>
            <Col span={12} className={"pl-12"}>
              <Form.Item<CreateLicenseRegistrationRequest>
                name="registrantEmail"
                label={t("field.email")}
              >
                <Input disabled />
              </Form.Item>
              <Form.Item<CreateLicenseRegistrationRequest>
                name="productCode"
                label={t("field.product")}
                className={"hidden"}
              >
                <Input disabled />
              </Form.Item>
              <Form.Item label={t("field.productName")}>
                <Input
                  value={detailRegistration?.productName || undefined}
                  disabled
                />
              </Form.Item>
              <Form.Item<CreateLicenseRegistrationRequest>
                name="departmentName"
                label={t("field.departmentName")}
              >
                <Input disabled />
              </Form.Item>
              <Form.Item<CreateLicenseRegistrationRequest>
                name="numberOfLicense"
                label={t("field.numberOfLicense")}
                rules={[validators.required(t("field.numberOfLicense"))]}
              >
                <InputNumber
                  className={"w-full"}
                  min={0}
                  max={1000}
                  disabled={true}
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item<CreateLicenseRegistrationRequest>
            name="addressMac"
            label={t("field.addressMac")}
            rules={[
              validators.required(t("field.addressMac")),
              validators.validateAddressMacs(),
            ]}
          >
            <Input.TextArea
              rows={5}
              placeholder={t("placeholder.enterAddressMacList")}
              onChange={changeNumberOfLicenseByNumberOfMac}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default ExtendButton;
